import { useEffect, useState, type RefObject } from "react";

export interface ExternalSubtitleTrack {
  src: string;
  srcLang: string;
  label: string;
  default?: boolean;
}

interface UseExternalSubtitlesParams {
  videoRef: RefObject<HTMLVideoElement | null>;
  subtitleTracks: ExternalSubtitleTrack[];
  activeSubtitleLanguage?: string;
}

export function useExternalSubtitles({
  videoRef,
  subtitleTracks,
  activeSubtitleLanguage,
}: UseExternalSubtitlesParams) {
  const [cueText, setCueText] = useState("");

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !subtitleTracks.length) {
      setCueText("");
      return;
    }

    let activeTrack: TextTrack | null = null;

    const syncCue = () => {
      const currentCue = activeTrack?.activeCues?.[0] as
        | (TextTrackCue & { text?: string })
        | undefined;
      setCueText(currentCue?.text ?? "");
    };

    const attachTrack = () => {
      Array.from(video.textTracks).forEach((track) => {
        track.mode = "disabled";
      });

      const targetLanguage =
        activeSubtitleLanguage ||
        subtitleTracks.find((track) => track.default)?.srcLang ||
        subtitleTracks[0]?.srcLang;

      activeTrack =
        Array.from(video.textTracks).find(
          (track) =>
            (track.kind === "subtitles" || track.kind === "captions") &&
            track.language === targetLanguage,
        ) ?? null;

      if (!activeTrack) {
        setCueText("");
        return;
      }

      activeTrack.mode = "hidden";
      activeTrack.addEventListener("cuechange", syncCue);
      video.addEventListener("timeupdate", syncCue);
      video.addEventListener("seeked", syncCue);
      syncCue();
    };

    const cleanup = () => {
      if (activeTrack) {
        activeTrack.removeEventListener("cuechange", syncCue);
      }
      video.removeEventListener("timeupdate", syncCue);
      video.removeEventListener("seeked", syncCue);
    };

    video.addEventListener("loadedmetadata", attachTrack);
    video.addEventListener("loadeddata", attachTrack);

    if (video.readyState >= 1) {
      attachTrack();
    }

    return () => {
      cleanup();
      video.removeEventListener("loadedmetadata", attachTrack);
      video.removeEventListener("loadeddata", attachTrack);
      setCueText("");
    };
  }, [activeSubtitleLanguage, subtitleTracks, videoRef]);

  return { cueText, setCueText };
}
